import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Player {
  id: string;
  name: string;
  jerseyNumber?: number;
  position?: string;
}

interface MatchEvent {
  id: string;
  type: 'goal' | 'assist' | 'yellow_card' | 'red_card';
  playerId: string;
  teamId: string;
  minute?: number;
  notes?: string;
}

interface Match {
  id: string;
  leagueId?: string;
  refereeId?: string;
  homeTeamId: string;
  awayTeamId: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  status: string;
  homeScore?: number;
  awayScore?: number;
  notes?: string;
  homeTeam: { id: string; name: string } | null;
  awayTeam: { id: string; name: string } | null;
  league: { id: string; name: string } | null;
  homeTeamMembers: Player[];
  awayTeamMembers: Player[];
  events: MatchEvent[];
}

const MatchDetail: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);

  // Admin reschedule state
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');

  // Admin reassign referee state
  const [assigningRef, setAssigningRef] = useState(false);
  const [referees, setReferees] = useState<Array<{ id: string; firstName: string; lastName: string }>>([]);
  const [selectedRefereeId, setSelectedRefereeId] = useState('');

  useEffect(() => {
    if (user?.profileType !== 'referee' && !isAdminOrSuperAdmin(user?.profileType)) {
      navigate('/dashboard');
      return;
    }

    fetchMatch();
  }, [matchId, user, navigate]);

  const fetchMatch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/referees/matches/${matchId}`);
      setMatch(response.data);
    } catch (error) {
      console.error('Error fetching match:', error);
      alert('Failed to load match details');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = isAdminOrSuperAdmin(user?.profileType);

  const fetchReferees = async (leagueId?: string) => {
    try {
      const response = await api.get('/users');
      const allUsers = response.data.users || response.data || [];
      const refereeUsers = allUsers.filter((u: any) => u.profileType === 'referee');

      let filteredReferees = refereeUsers;
      if (leagueId) {
        filteredReferees = refereeUsers.filter((ref: any) => {
          if (!ref.allowedLeagueIds || ref.allowedLeagueIds.length === 0) return true;
          return ref.allowedLeagueIds.includes(leagueId);
        });
      }

      setReferees(filteredReferees.map((u: any) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName })));
    } catch (error) {
      console.error('Error fetching referees:', error);
    }
  };

  const handleReschedule = async () => {
    if (!match) return;
    try {
      const body: any = {};
      if (editDate && editDate !== match.scheduledDate) body.scheduledDate = editDate;
      if (editTime && editTime !== match.scheduledTime) body.scheduledTime = editTime;
      if (!body.scheduledDate && !body.scheduledTime) {
        setEditing(false);
        return;
      }
      await api.patch(`/matches/${matchId}/reschedule`, body);
      setEditing(false);
      fetchMatch();
    } catch (error: any) {
      console.error('Error rescheduling match:', error);
      alert(error.response?.data?.error || 'Failed to reschedule match');
    }
  };

  const handleAssignReferee = async () => {
    try {
      await api.patch(`/matches/${matchId}/assign-referee`, {
        refereeId: selectedRefereeId || null
      });
      setAssigningRef(false);
      setSelectedRefereeId('');
      fetchMatch();
    } catch (error: any) {
      console.error('Error assigning referee:', error);
      alert(error.response?.data?.error || 'Failed to assign referee');
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!match) {
    return <div className="container">Match not found</div>;
  }

  const homeTeamPlayers = match.homeTeamMembers || [];
  const awayTeamPlayers = match.awayTeamMembers || [];
  const goalEvents = match.events.filter(e => e.type === 'goal');
  const assistEvents = match.events.filter(e => e.type === 'assist');
  const yellowCardEvents = match.events.filter(e => e.type === 'yellow_card');
  const redCardEvents = match.events.filter(e => e.type === 'red_card');

  const getPlayerName = (playerId: string) => {
    const player = [...homeTeamPlayers, ...awayTeamPlayers].find(p => p.id === playerId);
    return player?.name || 'Unknown';
  };

  const getTeamName = (teamId: string) => {
    if (teamId === match.homeTeamId) return match.homeTeam?.name || 'Home Team';
    if (teamId === match.awayTeamId) return match.awayTeam?.name || 'Away Team';
    return 'Unknown Team';
  };

  return (
    <div className="container">
      <div style={{ marginBottom: '20px' }}>
        <Link to="/referees/dashboard" className="btn btn-secondary">← Back to Dashboard</Link>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h1>
          {match.homeTeam?.name || 'TBD'} vs {match.awayTeam?.name || 'TBD'}
        </h1>
        {match.league && (
          <p style={{ color: '#757575', fontSize: '16px' }}>{match.league.name}</p>
        )}
        <div style={{ marginTop: '15px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <p><strong>Date:</strong> {new Date(match.scheduledDate).toLocaleDateString()}</p>
            <p><strong>Time:</strong> {match.scheduledTime}</p>
          </div>
          <div>
            <p><strong>Location:</strong> {match.location}</p>
            <p><strong>Status:</strong> <span className={`badge ${match.status === 'completed' ? 'badge-success' : match.status === 'scheduled' ? 'badge-info' : 'badge-warning'}`}>{match.status}</span></p>
          </div>
          {isAdmin && (
            <div>
              <p>
                <strong>Referee:</strong>{' '}
                {match.refereeId ? 'Assigned' : <span style={{ color: '#757575' }}>Not assigned</span>}
                <button
                  className="btn btn-secondary"
                  style={{ marginLeft: '10px', fontSize: '12px', padding: '4px 10px' }}
                  onClick={() => {
                    if (assigningRef) {
                      setAssigningRef(false);
                    } else {
                      setAssigningRef(true);
                      fetchReferees(match.leagueId);
                      setSelectedRefereeId(match.refereeId || '');
                    }
                  }}
                >
                  {assigningRef ? 'Cancel' : match.refereeId ? 'Change Ref' : 'Assign Ref'}
                </button>
              </p>
              {assigningRef && (
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    value={selectedRefereeId}
                    onChange={(e) => setSelectedRefereeId(e.target.value)}
                    style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                  >
                    <option value="">Unassign</option>
                    {referees.map(r => (
                      <option key={r.id} value={r.id}>{r.firstName} {r.lastName}</option>
                    ))}
                  </select>
                  <button className="btn btn-primary" style={{ fontSize: '12px', padding: '4px 10px' }} onClick={handleAssignReferee}>
                    Save
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {isAdmin && match.status === 'scheduled' && (
          <div style={{ marginTop: '15px' }}>
            {!editing ? (
              <button
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '6px 14px' }}
                onClick={() => {
                  setEditing(true);
                  setEditDate(match.scheduledDate);
                  setEditTime(match.scheduledTime || '');
                }}
              >
                Reschedule
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '12px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '13px' }}>Date</label>
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '13px' }}>Time</label>
                  <input
                    type="time"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    style={{ padding: '8px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
                  />
                </div>
                <button className="btn btn-primary" style={{ fontSize: '13px', padding: '8px 16px' }} onClick={handleReschedule}>
                  Save
                </button>
                <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '8px 16px' }} onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {match.status === 'completed' && match.homeScore !== undefined && match.awayScore !== undefined && (
          <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
            <h2 style={{ fontSize: '2rem', margin: '0' }}>
              {match.homeScore} - {match.awayScore}
            </h2>
          </div>
        )}
      </div>

      {match.status === 'completed' && (
        <>
          <div className="card" style={{ marginBottom: '20px' }}>
            <h2>Match Events</h2>
            
            {goalEvents.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3>⚽ Goals</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Team</th>
                      <th>Minute</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goalEvents.map((event, idx) => (
                      <tr key={idx}>
                        <td>{getPlayerName(event.playerId)}</td>
                        <td>{getTeamName(event.teamId)}</td>
                        <td>{event.minute || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {assistEvents.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3>🎯 Assists</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Team</th>
                      <th>Minute</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assistEvents.map((event, idx) => (
                      <tr key={idx}>
                        <td>{getPlayerName(event.playerId)}</td>
                        <td>{getTeamName(event.teamId)}</td>
                        <td>{event.minute || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {yellowCardEvents.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3>🟨 Yellow Cards</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Team</th>
                      <th>Minute</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yellowCardEvents.map((event, idx) => (
                      <tr key={idx}>
                        <td>{getPlayerName(event.playerId)}</td>
                        <td>{getTeamName(event.teamId)}</td>
                        <td>{event.minute || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {redCardEvents.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3>🟥 Red Cards</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Team</th>
                      <th>Minute</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redCardEvents.map((event, idx) => (
                      <tr key={idx}>
                        <td>{getPlayerName(event.playerId)}</td>
                        <td>{getTeamName(event.teamId)}</td>
                        <td>{event.minute || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {match.events.length === 0 && (
              <p style={{ color: '#757575' }}>No events recorded for this match.</p>
            )}
          </div>

          {match.notes && (
            <div className="card">
              <h2>Notes</h2>
              <p>{match.notes}</p>
            </div>
          )}
        </>
      )}

      {match.status === 'scheduled' && (
        <div className="card">
          <Link
            to={`/referees/matches/${match.id}/complete`}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            Complete Match
          </Link>
        </div>
      )}
    </div>
  );
};

export default MatchDetail;

